import { BookOpen, Sprout, Building2, Compass, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const pillars = [
  {
    icon: BookOpen,
    title: "Credit Education",
    description:
      "Understand credit reports, credit scores and the factors that may influence creditworthiness.",
    topics: ["Payment history", "Credit utilization", "Account age", "Credit inquiries", "Credit mix", "Reporting fundamentals"],
    testId: "teach-card-credit-education",
  },
  {
    icon: Sprout,
    title: "Credit Building Fundamentals",
    description:
      "Learn responsible habits that may help support a healthier financial profile over time.",
    topics: ["Payment management", "Utilization strategy", "Credit account organization", "Responsible borrowing", "Financial discipline"],
    testId: "teach-card-credit-building",
  },
  {
    icon: Building2,
    title: "Business Credit Education",
    description:
      "Understand how personal and business credit differ and how businesses can develop stronger financial foundations.",
    topics: ["Business credit fundamentals", "Business financial organization", "Lender readiness", "Business documentation", "Credit profiles"],
    testId: "teach-card-business-credit",
  },
  {
    icon: Compass,
    title: "Funding Readiness",
    description:
      "Learn what lenders may evaluate before making funding decisions and how to better prepare before submitting applications.",
    topics: ["Personal profile readiness", "Business profile readiness", "Documentation", "Cash flow awareness", "Application preparation", "Lender requirements"],
    testId: "teach-card-funding-readiness",
  },
];

export const WhatWeTeach = () => (
  <section id="what-we-teach" data-testid="what-we-teach-section" className="scroll-mt-24 bg-[#0E0E12] py-14 lg:py-20">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D8B54A]" data-testid="what-we-teach-label">
          What J11 Teaches
        </p>
        <div className="mt-3 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <h2
            data-testid="what-we-teach-heading"
            className="max-w-xl font-heading text-2xl font-bold tracking-tight text-[#F2EEE3] sm:text-3xl lg:text-4xl"
          >
            Understand the System Before Making Financial Decisions.
          </h2>
          <p className="max-w-md text-sm leading-relaxed text-[#9E978A] sm:text-base">
            Credit and funding can feel complicated when nobody has explained how the system actually
            works. J11 Consulting helps clients understand the fundamentals, identify areas they can
            work on and make more informed financial decisions.
          </p>
        </div>
      </Reveal>
      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        {pillars.map((pillar, i) => (
          <Reveal key={pillar.title} delay={i * 0.08}>
            <div
              data-testid={pillar.testId}
              className="group h-full rounded-2xl border border-white/10 bg-[#17171C] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#C9A227]/30 hover:shadow-xl hover:shadow-black/40 sm:p-8"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#141419] transition-colors duration-300 group-hover:bg-[#C9A227]/10">
                  <pillar.icon className="h-5 w-5 text-[#D8B54A]" strokeWidth={1.8} />
                </span>
                <h3 className="font-heading text-lg font-semibold tracking-tight text-[#F2EEE3] sm:text-xl">
                  {pillar.title}
                </h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#B8B2A6] sm:text-base">{pillar.description}</p>
              <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                {pillar.topics.map((topic) => (
                  <li key={topic} className="flex items-center gap-2 text-sm text-[#B8B2A6]">
                    <Check className="h-3.5 w-3.5 shrink-0 text-[#D8B54A]" strokeWidth={2.5} />
                    {topic}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
